import { Autocomplete, CircularProgress, Paper, Popper, TextField } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChoiceOption } from "../hooks/useChoiceOptions";
import { resolveDisplayText } from "../services/recordMetadataLabels";

interface ChoiceAutocompleteInputProps {
    options: ChoiceOption[];
    value: string | null | undefined;
    loading?: boolean;
    onChange: (value: string | null) => void;
    error?: boolean;
    helperText?: ReactNode;
    autoFocus?: boolean;
    fullWidth?: boolean;
    size?: "small" | "medium";
    disabled?: boolean;
    required?: boolean;
    fieldLabel?: string;
    columnTypePicker?: boolean;
}

export function ChoiceAutocompleteInput({
    options,
    value,
    loading = false,
    onChange,
    error = false,
    helperText,
    autoFocus = false,
    fullWidth = true,
    size = "small",
    disabled = false,
    required = false,
    fieldLabel,
    columnTypePicker = false,
}: ChoiceAutocompleteInputProps) {
    const { t } = useTranslation();
    const selectedOption =
        options.find((option) => option.value === value) ?? null;
    const optionLabel = (option: ChoiceOption) => option.label ? resolveDisplayText(t, option.label) : "";
    const [inputValue, setInputValue] = useState("");
    const matchingInputOption = () => {
        const normalizedInput = inputValue.trim().toLocaleLowerCase();
        if (!normalizedInput) return undefined;
        const matches = options.filter((option) => optionLabel(option).toLocaleLowerCase().includes(normalizedInput));
        return matches.find((candidate) => optionLabel(candidate).toLocaleLowerCase() === normalizedInput)
            || (matches.length === 1 ? matches[0] : undefined);
    };

    useEffect(() => {
        setInputValue(selectedOption ? optionLabel(selectedOption) : "");
    }, [selectedOption]);

    return (
        <Autocomplete
            options={columnTypePicker ? [...options].sort((a, b) => optionLabel(a).localeCompare(optionLabel(b))) : options}
            disableClearable={required}
            openText={fieldLabel ? `${t("Open")} ${fieldLabel}` : undefined}
            clearText={fieldLabel ? `${t("Clear")} ${fieldLabel}` : undefined}
            PopperComponent={columnTypePicker ? (props) => (
                <Popper {...props} placement="bottom-start" modifiers={[{ name: "flip", options: { fallbackPlacements: ["top-start"] } }]} />
            ) : undefined}
            PaperComponent={columnTypePicker ? (props) => (
                <Paper {...props} sx={{ "& .MuiAutocomplete-option[aria-selected='true']": { backgroundColor: "var(--prm-selected-bg)", color: "var(--prm-text-on-selected)" } }} />
            ) : undefined}
            loading={loading}
            value={selectedOption}
            inputValue={inputValue}
            clearOnBlur={false}
            disabled={disabled}
            onChange={(_, option) => onChange(option?.value ?? null)}
            onInputChange={(_, nextInputValue) => setInputValue(nextInputValue)}
            onBlur={() => {
                const option = matchingInputOption();
                if (option) queueMicrotask(() => onChange(option.value));
            }}
            onKeyDown={(event) => {
                if (event.key !== "Enter" || !inputValue.trim()) return;
                const option = matchingInputOption();
                if (!option) return;
                (event as typeof event & { defaultMuiPrevented?: boolean }).defaultMuiPrevented = true;
                onChange(option.value);
                setInputValue(optionLabel(option));
            }}
            getOptionLabel={optionLabel}
            isOptionEqualToValue={(option, currentValue) => option.value === currentValue.value}
            renderInput={(params) => (
                <TextField
                    {...params}
                    autoFocus={autoFocus}
                    size={size}
                    fullWidth={fullWidth}
                    error={error}
                    helperText={helperText}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading && <CircularProgress size={18} />}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
}
